import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useMemo } from 'react';
import { addDays, startOfDay, endOfDay } from 'date-fns';

export interface NextTraining {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  modality: string;
  type: string;
  class_type: string;
  description?: string;
}

export const useNextTrainings = () => {
  const { user } = useAuth();

  // Smart date selection: prioritize today if there are available slots
  const selectedDate = useMemo(() => {
    const today = new Date();
    const now = new Date();
    
    // Check if we're still early in the day (before 8 PM)
    // This allows showing today's slots if there are still reasonable hours left
    const currentHour = now.getHours();
    const isStillEarly = currentHour < 20; // Before 8 PM
    
    if (isStillEarly) {
      return today;
    }
    
    // If it's late in the day, look for tomorrow
    return addDays(today, 1);
  }, []);

  return useQuery({
    queryKey: ['next-trainings', user?.id, selectedDate.toDateString()],
    queryFn: async (): Promise<NextTraining[]> => {
      if (!user) return [];

      try {
        // First, try to get trainings for today
        const today = new Date();
        const startOfToday = startOfDay(today);
        const endOfToday = endOfDay(today);
        
        let { data, error } = await supabase
          .from('trainings')
          .select(`
            id,
            title,
            start_time,
            end_time,
            modality,
            type,
            class_type,
            description
          `)
          .eq('user_id', user.id)
          .eq('status', 'Scheduled')
          .gte('start_time', startOfToday.toISOString())
          .lte('start_time', endOfToday.toISOString())
          .order('start_time', { ascending: true })
          .limit(3);

        if (error) {
          console.error('Error fetching today\'s trainings:', error);
          throw error;
        }

        // If we have trainings today, return them
        if (data && data.length > 0) {
          return data;
        }

        // If no trainings today, get trainings for the selected date (usually tomorrow)
        const startOfSelectedDay = startOfDay(selectedDate);
        const endOfSelectedDay = endOfDay(selectedDate);
        
        ({ data, error } = await supabase
          .from('trainings')
          .select(`
            id,
            title,
            start_time,
            end_time,
            modality,
            type,
            class_type,
            description
          `)
          .eq('user_id', user.id)
          .eq('status', 'Scheduled')
          .gte('start_time', startOfSelectedDay.toISOString())
          .lte('start_time', endOfSelectedDay.toISOString())
          .order('start_time', { ascending: true })
          .limit(3));

        if (error) {
          console.error('Error fetching selected date trainings:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error in useNextTrainings:', error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
